/** Heuristics for optimization-first AI SEO Optimizer overview. */

function sumField(pages, field) {
  return pages.reduce((sum, p) => sum + (p[field] || 0), 0);
}

function countAffected(pages, predicate) {
  return pages.filter(predicate).length;
}

export function estimateProjectedScore(currentScore, fixableIssues) {
  if (fixableIssues <= 0) return currentScore;
  const gain = Math.min(25, Math.max(3, Math.ceil(fixableIssues * 0.22)));
  return Math.min(100, currentScore + gain);
}

export function estimateBulkTimeSeconds(selectedCount) {
  if (selectedCount <= 0) return '—';
  const seconds = Math.max(15, Math.ceil(selectedCount * 3.75));
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

export function estimateSelectedScoreGain(pages, selectedIds) {
  const selected = pages.filter((p) => selectedIds.has(p.id));
  const issues = selected.reduce(
    (sum, p) => sum + (p.meta || 0) + (p.content || 0) + (p.image || 0),
    0
  );
  return Math.min(20, Math.max(1, Math.ceil(issues * 0.35)));
}

export function computeAiOpportunitySummary(pages, totalErrors, currentScore) {
  const metaIssues = sumField(pages, 'meta');
  const contentIssues = sumField(pages, 'content');
  const imageIssues = sumField(pages, 'image');
  const titleIssues = countAffected(
    pages,
    (p) => p.meta > 0 && String(p.type).toLowerCase() === 'product'
  );
  const fixableByAi = metaIssues + contentIssues + imageIssues;

  return {
    totalIssues: totalErrors,
    metaIssues,
    contentIssues,
    titleIssues,
    imageIssues,
    currentScore,
    projectedScore: estimateProjectedScore(currentScore, fixableByAi),
    timeEstimate: fixableByAi > 50 ? '~2 minutes' : 'Less than 1 minute',
    fixableByAi,
  };
}

export function computeHighImpactOpportunities(pages) {
  const metaAffected = countAffected(pages, (p) => p.meta > 0);
  const contentAffected = countAffected(pages, (p) => p.content > 0);
  const imageAffected = countAffected(pages, (p) => p.image > 0);
  const titleAffected = countAffected(
    pages,
    (p) => p.meta > 0 && String(p.type).toLowerCase() === 'product'
  );

  const buckets = [
    {
      id: 'meta',
      label: 'Metadata optimization',
      affectedCount: metaAffected,
      issueCount: sumField(pages, 'meta'),
      scoreGain: Math.min(12, Math.max(2, Math.ceil(metaAffected * 0.55))),
      impact: 'High',
    },
    {
      id: 'content',
      label: 'Content optimization',
      affectedCount: contentAffected,
      issueCount: sumField(pages, 'content'),
      scoreGain: Math.min(10, Math.max(2, Math.ceil(contentAffected * 0.5))),
      impact: 'High',
    },
    {
      id: 'title',
      label: 'Product titles',
      affectedCount: titleAffected,
      issueCount: titleAffected,
      scoreGain: Math.min(6, Math.max(1, Math.ceil(titleAffected * 0.4))),
      impact: 'Medium',
    },
    {
      id: 'image',
      label: 'Image SEO',
      affectedCount: imageAffected,
      issueCount: sumField(pages, 'image'),
      scoreGain: Math.min(5, Math.max(1, Math.ceil(imageAffected * 0.35))),
      impact: 'Medium',
    },
  ];

  return buckets.filter((b) => b.affectedCount > 0).sort((a, b) => b.scoreGain - a.scoreGain);
}

export function pageNeedsAiFix(page) {
  const issueCount =
    (page.meta || 0) + (page.content || 0) + (page.image || 0) + (page.broken || 0) + (page.url || 0);
  return issueCount > 0;
}

export function pickFirstAiFixTarget(pages) {
  const candidates = pages
    .filter(pageNeedsAiFix)
    .sort((a, b) => (a.seoScore || 0) - (b.seoScore || 0));
  return candidates[0] ?? null;
}

export function computePageAiRecommendation(page) {
  const totalIssues =
    (page.meta || 0) + (page.content || 0) + (page.image || 0) + (page.broken || 0) + (page.url || 0);

  if (totalIssues === 0 && page.seoScore >= 95) {
    return 'No AI fixes needed';
  }
  if (page.broken > 0) {
    return 'Fix broken links';
  }
  if (page.url > 0) {
    return 'Fix URL issues';
  }
  if (page.meta > 0 && page.content > 0) {
    return 'Rewrite title + description';
  }
  if (page.meta > 0) {
    return page.meta >= 2 ? 'Rewrite title + description' : 'Generate meta description';
  }
  if (page.content > 0) {
    return 'Improve content structure';
  }
  if (page.image > 0) {
    return 'Generate image alt text';
  }
  return 'Review SEO settings';
}

export function pageMatchesOpportunityFilter(page, filter) {
  if (!filter) return true;
  if (filter === 'meta') return page.meta > 0;
  if (filter === 'content') return page.content > 0;
  if (filter === 'image') return page.image > 0;
  if (filter === 'title') {
    return page.meta > 0 && String(page.type).toLowerCase() === 'product';
  }
  return true;
}

/** Normalize Ecwid page rows for recommendation helpers. */
export function normalizeSeoPage(page) {
  const toNum = (v) => {
    if (typeof v === 'number') return v;
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  };
  return {
    ...page,
    id: page.id,
    name: page.pageName || page.name || '',
    type: page.pageType || page.type || 'product',
    meta: toNum(page.metaIssues ?? page.metaScore ?? page.meta),
    content: toNum(page.contentIssues ?? page.contentScore ?? page.content),
    image: toNum(page.imageIssues ?? page.imageScore ?? page.image),
    broken: toNum(page.brokenLinks ?? page.broken),
    url: toNum(page.urlIssues ?? page.urlScore ?? page.url),
    seoScore: toNum(page.seoScore),
    image_url: page.imageUrl || page.image_url || null,
  };
}
